import type {
  CreateGuestInviteInput,
  CreateLiveInput,
  CreateShowInput,
  GuestInvite,
  GuestLiveInfo,
  Live,
  LiveKitConnection,
  RecordingStatus,
  MuteParticipantInput,
  ParticipantTrackState,
  Show,
  UpdateLiveInput,
  UpdateShowInput,
  User,
  YouTubeAuthUrl,
  YouTubeConnectionStatus,
} from '@liveforge/shared';
import { apiRequest, apiRequestVoid } from './api';

/* -------------------------------------------------------------------------- */
/* Auth                                                                       */
/* -------------------------------------------------------------------------- */

/** Upserts the local `users` row for the currently signed-in Firebase user. */
export function login(): Promise<User> {
  return apiRequest<User>('/auth/login', { method: 'POST' });
}

/* -------------------------------------------------------------------------- */
/* Shows                                                                      */
/* -------------------------------------------------------------------------- */

export function listShows(signal?: AbortSignal): Promise<Show[]> {
  return apiRequest<Show[]>('/shows', { signal });
}

export function getShow(showId: string, signal?: AbortSignal): Promise<Show> {
  return apiRequest<Show>(`/shows/${showId}`, { signal });
}

export function createShow(input: CreateShowInput): Promise<Show> {
  return apiRequest<Show>('/shows', { method: 'POST', body: input });
}

export function updateShow(showId: string, input: UpdateShowInput): Promise<Show> {
  return apiRequest<Show>(`/shows/${showId}`, { method: 'PUT', body: input });
}

export function deleteShow(showId: string): Promise<void> {
  return apiRequestVoid(`/shows/${showId}`, { method: 'DELETE' });
}

/* -------------------------------------------------------------------------- */
/* YouTube binding                                                            */
/* -------------------------------------------------------------------------- */

export function getYouTubeStatus(
  showId: string,
  signal?: AbortSignal,
): Promise<YouTubeConnectionStatus> {
  return apiRequest<YouTubeConnectionStatus>(`/shows/${showId}/youtube`, { signal });
}

/**
 * Asks for the consent URL. The caller navigates there itself - the API
 * answers with a URL rather than a redirect, because a 302 to Google inside a
 * fetch would be followed invisibly and the user would never see the screen.
 */
export function connectYouTube(showId: string): Promise<YouTubeAuthUrl> {
  return apiRequest<YouTubeAuthUrl>(`/shows/${showId}/youtube/connect`, { method: 'POST' });
}

export function disconnectYouTube(showId: string): Promise<void> {
  return apiRequestVoid(`/shows/${showId}/youtube`, { method: 'DELETE' });
}

/* -------------------------------------------------------------------------- */
/* Lives                                                                      */
/* -------------------------------------------------------------------------- */

export function listLives(showId: string, signal?: AbortSignal): Promise<Live[]> {
  return apiRequest<Live[]>(`/shows/${showId}/lives`, { signal });
}

export function createLive(showId: string, input: CreateLiveInput): Promise<Live> {
  return apiRequest<Live>(`/shows/${showId}/lives`, { method: 'POST', body: input });
}

export function getLive(liveId: string, signal?: AbortSignal): Promise<Live> {
  return apiRequest<Live>(`/lives/${liveId}`, { signal });
}

export function updateLive(liveId: string, input: UpdateLiveInput): Promise<Live> {
  return apiRequest<Live>(`/lives/${liveId}`, { method: 'PUT', body: input });
}

export function deleteLive(liveId: string): Promise<void> {
  return apiRequestVoid(`/lives/${liveId}`, { method: 'DELETE' });
}

/* -------------------------------------------------------------------------- */
/* Guest invites                                                              */
/* -------------------------------------------------------------------------- */

export function listGuestInvites(liveId: string, signal?: AbortSignal): Promise<GuestInvite[]> {
  return apiRequest<GuestInvite[]>(`/lives/${liveId}/guests`, { signal });
}

export function createGuestInvite(
  liveId: string,
  input: CreateGuestInviteInput,
): Promise<GuestInvite> {
  return apiRequest<GuestInvite>(`/lives/${liveId}/guests`, { method: 'POST', body: input });
}

export function deleteGuestInvite(inviteId: string): Promise<void> {
  return apiRequestVoid(`/guests/${inviteId}`, { method: 'DELETE' });
}

/* -------------------------------------------------------------------------- */
/* LiveKit tokens                                                             */
/* -------------------------------------------------------------------------- */

export function getHostConnection(
  liveId: string,
  signal?: AbortSignal,
): Promise<LiveKitConnection> {
  return apiRequest<LiveKitConnection>(`/lives/${liveId}/token`, { method: 'POST', signal });
}

/* -------------------------------------------------------------------------- */
/* Guest (unauthenticated)                                                    */
/* -------------------------------------------------------------------------- */

export function getGuestLiveInfo(token: string, signal?: AbortSignal): Promise<GuestLiveInfo> {
  return apiRequest<GuestLiveInfo>(`/guest/${encodeURIComponent(token)}`, {
    authenticated: false,
    signal,
  });
}

export function getGuestConnection(
  token: string,
  signal?: AbortSignal,
): Promise<LiveKitConnection> {
  return apiRequest<LiveKitConnection>(`/guest/${encodeURIComponent(token)}/join`, {
    authenticated: false,
    signal,
  });
}

/* -------------------------------------------------------------------------- */
/* Room administration (host only)                                            */
/* -------------------------------------------------------------------------- */

/**
 * Mutes or unmutes a participant's track. Room administration cannot be done
 * from the browser, whatever the token grants, so it goes through the API.
 */
export function muteParticipant(
  liveId: string,
  identity: string,
  input: MuteParticipantInput,
): Promise<ParticipantTrackState> {
  return apiRequest<ParticipantTrackState>(
    `/lives/${liveId}/participants/${encodeURIComponent(identity)}/mute`,
    { method: 'POST', body: input },
  );
}

/* -------------------------------------------------------------------------- */
/* Recording                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * `debug` asks Egress to load the compositing page with its diagnostic overlay,
 * which is then burnt into the file - the only way to see what the compositor
 * could see at a moment that has already passed.
 */
export function startRecording(liveId: string, debug = false): Promise<RecordingStatus> {
  // `send` stringifies the body itself; handing it a string would encode the
  // JSON twice and the backend's zod would reject a quoted blob.
  return apiRequest<RecordingStatus>(`/lives/${liveId}/recording/start`, {
    method: 'POST',
    body: debug ? { debug: true } : undefined,
  });
}

export function stopRecording(liveId: string): Promise<RecordingStatus> {
  return apiRequest<RecordingStatus>(`/lives/${liveId}/recording/stop`, { method: 'POST' });
}

/**
 * Forgets a job the media server never started, so a new one can be asked for.
 * It does not promise anything was stopped - see the service that answers it.
 */
export function abandonRecording(liveId: string): Promise<RecordingStatus> {
  return apiRequest<RecordingStatus>(`/lives/${liveId}/recording/abandon`, { method: 'POST' });
}

export function getRecordingStatus(liveId: string, signal?: AbortSignal): Promise<RecordingStatus> {
  return apiRequest<RecordingStatus>(`/lives/${liveId}/recording/status`, { signal });
}

/**
 * A short-lived, signed URL the browser can follow on its own.
 *
 * Ownership is checked when the link is minted, not when it is followed: a
 * plain download sends no headers, and buffering a multi-gigabyte MP4 into a
 * blob to attach one would run the tab out of memory.
 */
export function recordingDownloadLink(liveId: string): Promise<{ url: string }> {
  return apiRequest<{ url: string }>(`/lives/${liveId}/recording/link`, { method: 'POST' });
}

/**
 * Mints the subscribe-only, hidden token the compositing page connects with.
 *
 * The same token Egress will be handed in Phase 5. Opening the Layout Page by
 * hand with it is the only way to see the composition rendered by something
 * that is not the studio, which is the property the whole output pipeline
 * rests on.
 */
export function egressToken(liveId: string): Promise<LiveKitConnection> {
  return apiRequest<LiveKitConnection>(`/lives/${liveId}/egress-token`, { method: 'POST' });
}

/** Disconnects a participant. Their invite link stays valid. */
export function removeParticipant(liveId: string, identity: string): Promise<void> {
  return apiRequestVoid(`/lives/${liveId}/participants/${encodeURIComponent(identity)}`, {
    method: 'DELETE',
  });
}
